import { DashboardRoutes } from "@/router/constant";
import { Alert, Spin, Typography } from "@payping/web-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";

import AddNewIBANModal from "./AddNewIBANModal";
import { EmptyBankState, PendingReviewState, SingleIBANCard } from "./components";
import { useIBANManagement } from "./hooks/useIBANManagement";

const UserBank: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeIBAN, setActiveIBAN] = useState<string>("");
  const hasCheckedActiveIBAN = useRef(false);

  const {
    showAddIBANModal,
    addIBANForm,
    setShowAddIBANModal,
    handleAddIBAN,
    getActiveIBAN,
    loading,
    contextHolder,
    pendingIBANList,
    approvedIBANList,
    rejectedIBANList,
  } = useIBANManagement();

  const loadActiveIBAN = useCallback(async () => {
    // Skip if already checked or not on bank root page
    if (hasCheckedActiveIBAN.current) {
      return;
    }

    hasCheckedActiveIBAN.current = true;

    try {
      const response = await getActiveIBAN();
      if (response?.iban) {
        setActiveIBAN(response.iban);
      } else {
        setActiveIBAN("");
        navigate(`${DashboardRoutes.settings.bank.edit.url}?noActiveIban=true`);
      }
    } catch (error) {
      setActiveIBAN("");
      navigate(`${DashboardRoutes.settings.bank.edit.url}?noActiveIban=true`);
    }
  }, [getActiveIBAN, navigate]);

  useEffect(() => {
    // Only check on mount when on bank root page
    if (location.pathname === DashboardRoutes.settings.bank.root.url) {
      hasCheckedActiveIBAN.current = false;
      loadActiveIBAN();
    }
  }, [location.pathname, loadActiveIBAN]);

  return (
    <>
      {contextHolder}
      <div className="tw-mt-6 tw-px-4 md:tw-mt-0 md:tw-px-0">
        <Typography.Title className="!tw-mb-8" level={4}>
          {t("profile.main.bank")}
        </Typography.Title>

        {(() => {
          const hasAnyIBANs = approvedIBANList.length > 0 || pendingIBANList.length > 0 || rejectedIBANList.length > 0;
          const isPendingReviewState = !activeIBAN && pendingIBANList.length > 0 && approvedIBANList.length === 0;
          const shouldShowAlert = !loading && (hasAnyIBANs || activeIBAN) && !isPendingReviewState;

          return shouldShowAlert ? (
            <Alert
              type="warning"
              message={t("profile.bank.settlementTimeWarning")}
              showIcon={false}
              className="tw-mb-6"
            />
          ) : null;
        })()}

        {loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
            <Spin size="large" />
          </div>
        )}

        {!loading && activeIBAN && (
          <SingleIBANCard sheba={activeIBAN} onEdit={() => navigate(DashboardRoutes.settings.bank.edit.url)} />
        )}

        {!loading && !activeIBAN && pendingIBANList.length > 0 && approvedIBANList.length === 0 && (
          <PendingReviewState onViewDetails={() => navigate(DashboardRoutes.settings.bank.edit.url)} />
        )}

        {!loading && !activeIBAN && (pendingIBANList.length === 0 || approvedIBANList.length > 0) && (
          <EmptyBankState onAddNew={() => setShowAddIBANModal(true)} />
        )}

        <AddNewIBANModal
          open={showAddIBANModal}
          onCancel={() => setShowAddIBANModal(false)}
          onFinish={handleAddIBAN}
          form={addIBANForm}
          enableMultipleIBANs={true}
        />
      </div>
    </>
  );
};

export default UserBank;
